import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useUser} from 'cor-business'
import styles from './index.module.scss'
import ListView from '../../../Project/components/ListView'
import TaskModal from '../../../../components/Modal/components/TaskModal'
import DropDown from '../../../../components/DropDown'
import DotsVerticalIcon from '../../../../assets/icon/DotsVerticalIcon'
import StatisticsReportIcon from '../../../../assets/icon/StatisticsReportIcon'
import {getTaskSpentHours} from '../../../../utils/taskSpentTime'
import HideWidgetIcon from '../../../../assets/icon/HideWidgetIcon'
import WidgetDragHandleIcon from '../../../../assets/icon/WidgetDragHandleIcon'
import ProjectService from '../../../../services/project.service'
import TaskService from '../../../../services/task.service'
import AttachmentService from '../../../../services/attachment.service'
import TimeLogService from '../../../../services/time-log.service'
import WorkspaceMembersService from '../../../../services/workspace-members.service'
import {useWorkspace} from '../../../../context/workspace.context'
import { extractList, getProjectMemberUserId, usersHaveSameIdentity } from '../../../../utils/members'
import {stripTaskTimeTrackingFields} from '../../../Project/utils/taskPayload'
import {persistRelatedTask} from '../../../../utils/persistRelatedTask'
import {
    PendingInlineImage,
    persistPendingInlineImages,
    removePendingInlineImages,
} from '../../../../utils/inlineTaskImages'
import { mergeMyTaskDetails, resolveMyTaskCreationAssigneeIds } from './taskData'

const extractProjects = (response: any): any[] => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.projects)) return response.projects
    if (Array.isArray(response?.items)) return response.items
    if (Array.isArray(response?.results)) return response.results
    if (Array.isArray(response?.data)) return response.data
    return []
}

const mapWidgetTask = (row: any, projects: any[], currentUser: any) => {
    const currentUserId = String(currentUser?.user_id ?? currentUser?.id ?? '')
    const taskId = row.task_id ?? row.id
    const projectId = row.project_id ?? row.project?.id
    const projectName = row.project_name ?? row.project?.name
    const project = projects.find((item) => String(item.id) === String(projectId))
        || {id: projectId, name: projectName || 'Проект'}
    const participants = Array.isArray(row.participants)
        ? row.participants
        : Array.isArray(row.assignees)
            ? row.assignees
            : Array.isArray(row.users)
                ? row.users
                : []
    const participantUsers = participants.map((participant: any, index: number) => (
        typeof participant === 'string'
            ? {id: `${taskId}-participant-${index}`, name: participant, full_name: participant}
            : participant
    ))
    const participantIds = participants
        .filter((participant: any) => typeof participant !== 'string')
        .map((participant: any) => participant?.id ?? participant?.user_id ?? participant?.user?.id)
        .filter(Boolean)
    const assigneeIds = Array.isArray(row.assignee_ids) && row.assignee_ids.length
        ? row.assignee_ids
        : participantIds.length
            ? participantIds
            : currentUserId
                ? [currentUserId]
                : []
    const hasCurrentUser = participantUsers.some((participant: any) => (
        String(participant?.user_id ?? participant?.id ?? participant?.user?.id ?? '') === currentUserId
    ))
    const users = currentUserId
        && assigneeIds.some((assigneeId: string | number) => String(assigneeId) === currentUserId)
        && !hasCurrentUser
        ? [...participantUsers, currentUser]
        : participantUsers

    return {
        ...row,
        id: taskId,
        title: row.task_name ?? row.title,
        due_date: row.deadline ?? row.due_date,
        created_at: row.date_created ?? row.created_at,
        project_id: projectId,
        project_name: projectName,
        project,
        users,
        assignee_ids: assigneeIds,
        created_by: row.creator ? {name: row.creator, full_name: row.creator} : null,
        spendTime: getTaskSpentHours(row),
        attachments_count: row.attachments_count || 0,
        comments_count: row.comments_count || 0,
        subtasks_count: row.subtasks_count || 0,
    }
}

export default function MyTasksWidget({onRemove, dragHandleProps}: {onRemove?: () => void; dragHandleProps?: any}) {
    const {workspace} = useWorkspace()
    const {user} = useUser()
    const [tasks, setTasks] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [projects, setProjects] = useState<any[]>([])
    const [creationProject, setCreationProject] = useState<any>(null)
    const [creationAssigneeId, setCreationAssigneeId] = useState<string>('')
    const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null)
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
    const [openedTask, setOpenedTask] = useState<any>(null)
    const loadTasksRequestRef = useRef(0)
    const openTaskRequestRef = useRef(0)
    const currentUserId = String(user?.user_id ?? user?.id ?? user?.profile?.user_id ?? '')
    const currentUserForTasks = useMemo(() => ({
        ...(user?.profile || {}),
        ...(user || {}),
        id: currentUserId,
        user_id: currentUserId,
        first_name: user?.first_name ?? user?.profile?.first_name ?? '',
        last_name: user?.last_name ?? user?.profile?.last_name ?? '',
        email: user?.email ?? user?.profile?.email ?? '',
        avatar: user?.avatar ?? user?.photo ?? user?.profile?.avatar ?? user?.profile?.photo ?? '',
    }), [
        currentUserId,
        user?.first_name,
        user?.last_name,
        user?.email,
        user?.avatar,
        user?.photo,
        user?.profile?.first_name,
        user?.profile?.last_name,
        user?.profile?.email,
        user?.profile?.avatar,
        user?.profile?.photo,
    ])

    const loadTasks = useCallback(async () => {
        const requestId = ++loadTasksRequestRef.current
        if (!workspace?.id) {
            setTasks([])
            setProjects([])
            setTotal(0)
            return
        }

        try {
            const [widgetResponse, projectsResponse] = await Promise.all([
                ProjectService.getMyTasksWidget({workspace_id: workspace.id, skip: 0, limit: 200}),
                ProjectService.list({workspace_id: workspace.id, skip: 0, limit: 100}).catch(() => []),
            ])
            const projects = extractProjects(projectsResponse)
            const rows = Array.isArray(widgetResponse?.items) ? widgetResponse.items : []
            const mappedTasks = rows.map((row: any) => mapWidgetTask(row, projects, currentUserForTasks))

            if (requestId !== loadTasksRequestRef.current) return

            setTasks(mappedTasks)
            setTotal(Number(widgetResponse?.total_tasks ?? mappedTasks.length))
            setProjects(projects)
            setCreationProject((currentProject: any) => (
                currentProject && projects.some((item) => String(item.id) === String(currentProject.id))
                    ? currentProject
                    : null
            ))
        } catch (error) {
            if (requestId !== loadTasksRequestRef.current) return
            console.error('Failed to load my tasks widget:', error)
        }
    }, [workspace?.id, currentUserForTasks])

    useEffect(() => {
        void loadTasks()
    }, [loadTasks])

    const openTask = useCallback(async (task: any) => {
        const requestId = ++openTaskRequestRef.current
        try {
            const projectId = task.project_id ?? task.project?.id
            const projectSummary = task.project || {
                id: projectId,
                name: task.project_name || 'Проект',
            }
            const shouldLoadStages = Number(task.subtasks_count ?? task.children_count ?? 0) > 0
            const [response, detailedProject, projectTasksResponse] = await Promise.all([
                TaskService.get(task.id),
                projectId != null
                    ? ProjectService.getProjectWithStatuses(projectSummary).catch(() => projectSummary)
                    : Promise.resolve(task.project),
                projectId != null && shouldLoadStages
                    ? ProjectService.getAllProjectTasks(String(projectId)).catch(() => null)
                    : Promise.resolve(null),
            ])
            const taskDetails = response?.task ?? response
            const projectTask = projectTasksResponse?.tasks?.find((currentTask: any) => (
                String(currentTask.id) === String(task.id)
            ))
            const details = mergeMyTaskDetails(task, taskDetails, projectTask)
            const detailedUsers = Array.isArray(details?.users)
                ? details.users
                : Array.isArray(details?.assignees)
                    ? details.assignees
                    : task.users
            const detailedAssigneeIds = Array.isArray(details?.assignee_ids) && details.assignee_ids.length
                ? details.assignee_ids
                : task.assignee_ids

            if (requestId !== openTaskRequestRef.current) return

            setOpenedTask({
                ...details,
                id: details?.id ?? task.id,
                project_id: details?.project_id ?? task.project_id,
                project: detailedProject || details?.project || task.project,
                users: detailedUsers,
                assignee_ids: detailedAssigneeIds,
            })
        } catch (error) {
            if (requestId !== openTaskRequestRef.current) return
            console.error('Failed to load task details:', error)
            setOpenedTask(task)
        }
    }, [])

    const openCreateTask = async (projectSummary: any) => {
        const projectId = projectSummary?.id
        if (projectId == null || loadingProjectId) return

        setLoadingProjectId(String(projectId))
        try {
            const [detailedProject, projectMembersResponse] = await Promise.all([
                ProjectService.getProjectWithStatuses(projectSummary),
                WorkspaceMembersService.getProjectMembers(Number(projectId), { limit: 1000 }).catch(() => []),
            ])
            const currentProjectMember = extractList(projectMembersResponse).find((member) => (
                usersHaveSameIdentity(member, currentUserForTasks)
            ))

            setCreationAssigneeId(getProjectMemberUserId(currentProjectMember) || currentUserId)
            setCreationProject(detailedProject)
            setIsTaskModalOpen(true)
        } catch (error) {
            console.error('Failed to load project before task creation:', error)
        } finally {
            setLoadingProjectId(null)
        }
    }

    const createTask = async (task: any) => {
        if (!creationProject?.id) throw new Error('Project is required to create a task')

        const {
            assignee_files: assigneeFiles = [],
            inline_images: inlineImages = [],
            deletedRelatedTaskIds = [],
            relatedTasks = [],
            ...taskFields
        } = task
        const assigneeIds = resolveMyTaskCreationAssigneeIds(
            taskFields.assignee_ids,
            creationAssigneeId || currentUserId,
        )
        const descriptionForCreate = inlineImages.length
            ? removePendingInlineImages(taskFields.description || '')
            : taskFields.description
        const newTask = await TaskService.create(stripTaskTimeTrackingFields({
            ...taskFields,
            description: descriptionForCreate,
            priority: taskFields.priority || 'medium',
            assignee_ids: assigneeIds,
            project_id: creationProject.id,
        }))

        await TimeLogService.syncForTask({
            taskId: newTask.id,
            spendTime: task.spendTime,
            description: task.timeLogDescription || '',
            timeLogId: task.timeLogId,
        })

        const uploadResults = await Promise.allSettled(
            assigneeFiles.map((file: any) => AttachmentService.upload(newTask.id, file)),
        )
        const failedUpload = uploadResults.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined
        if (failedUpload) {
            try {
                await TaskService.delete(newTask.id)
            } catch (rollbackError) {
                console.error('Failed to roll back task after attachment upload error:', rollbackError)
            }
            throw failedUpload.reason
        }

        if (inlineImages.length) {
            try {
                const resolvedDescription = await persistPendingInlineImages(
                    Number(newTask.id),
                    taskFields.description || '',
                    inlineImages as PendingInlineImage[],
                )
                await TaskService.update(newTask.id, stripTaskTimeTrackingFields({
                    ...taskFields,
                    description: resolvedDescription,
                    priority: taskFields.priority || 'medium',
                    assignee_ids: assigneeIds,
                    project_id: creationProject.id,
                }))
                newTask.description = resolvedDescription
            } catch (error) {
                try {
                    await TaskService.delete(newTask.id)
                } catch (rollbackError) {
                    console.error('Failed to roll back task after inline image upload error:', rollbackError)
                }
                throw error
            }
        }

        await Promise.all(deletedRelatedTaskIds.map((taskId: number | string) => TaskService.delete(Number(taskId))))
        await Promise.all(relatedTasks.filter(Boolean).map((relatedTask: any) => (
            persistRelatedTask({
                task: relatedTask,
                parentTaskId: newTask.id,
                projectId: creationProject.id,
            })
        )))

        await loadTasks()
        setIsTaskModalOpen(false)
        return newTask
    }

    const updateOpenedTask = async (taskData: any) => {
        if (!openedTask?.id) return null

        const projectId = taskData.project_id ?? openedTask.project_id ?? openedTask.project?.id
        const assigneeIds = Array.isArray(taskData.assignee_ids)
            ? taskData.assignee_ids
            : currentUserId
                ? [currentUserId]
                : []
        const updatePayload = stripTaskTimeTrackingFields({
            title: taskData.title || '',
            description: taskData.description || '',
            status: taskData.status || '',
            priority: taskData.priority || '',
            task_type: taskData.task_type || 'task',
            project_id: projectId,
            assignee_ids: assigneeIds,
            due_date: taskData.due_date || null,
            ...(taskData.parent_id != null ? {parent_id: taskData.parent_id} : {}),
        })

        await TaskService.update(openedTask.id, updatePayload)

        await Promise.all((taskData.deletedRelatedTaskIds || []).map((taskId: number | string) => (
            TaskService.delete(Number(taskId))
        )))
        await Promise.all((taskData.relatedTasks || []).filter(Boolean).map((relatedTask: any) => (
            persistRelatedTask({
                task: relatedTask,
                parentTaskId: openedTask.id,
                projectId,
            })
        )))

        setOpenedTask(null)
        await loadTasks()
        return taskData
    }

    const taskCountTitle = useMemo(() => `${total} ${total === 1 ? 'задача' : total >= 2 && total <= 4 ? 'задачи' : 'задач'}`, [total])
    const listProject = useMemo(() => (
        projects[0] || tasks.find((task: any) => task?.project)?.project || null
    ), [projects, tasks])

    return (
        <div className={styles.myTasks}>
            <div className={styles.myTasks__header}>
                <div className={styles.myTasks__title}>
                    <span className={styles.myTasks__drag} aria-label="Переместить виджет" {...dragHandleProps}><WidgetDragHandleIcon/></span>
                    <h3>Мои задачи</h3>
                </div>
                <div className={styles.myTasks__actions}>
                    <DropDown
                        position="right"
                        closeDropDownByPick
                        dropdownClassName={styles.myTasks__projectDropdown}
                        btn={(
                            <button
                                type="button"
                                className={styles.myTasks__add}
                                disabled={!projects.length || Boolean(loadingProjectId)}
                                aria-label="Выбрать проект и создать задачу"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                </svg>
                            </button>
                        )}
                    >
                        <div className={styles.myTasks__projectMenu}>
                            <div className={styles.myTasks__projectMenuTitle}>Создать задачу в проекте</div>
                            {projects.map((projectItem) => (
                                <button
                                    key={String(projectItem.id)}
                                    type="button"
                                    onClick={() => void openCreateTask(projectItem)}
                                >
                                    {projectItem.name || 'Проект'}
                                </button>
                            ))}
                        </div>
                    </DropDown>
                    {onRemove && (
                        <DropDown
                            position="right"
                            closeDropDownByPick
                            dropdownClassName={styles.myTasks__dropdown}
                            btn={(
                                <button type="button" className={styles.myTasks__more} aria-label="Действия с виджетом">
                                    <DotsVerticalIcon/>
                                </button>
                            )}
                        >
                            <div className={styles.myTasks__menu}>
                                <button type="button" disabled title="Формирование отчёта пока недоступно"><StatisticsReportIcon/><span>Сформировать в отчёт</span></button>
                                <button type="button" onClick={onRemove}><HideWidgetIcon/><span>Скрыть виджет</span></button>
                            </div>
                        </DropDown>
                    )}
                </div>
            </div>

            <div className={styles.myTasks__list}>
                <div className={styles.myTasks__scroll}>
                    {listProject && (
                        <ListView
                            tasks={tasks}
                            project={listProject}
                            getTasks={loadTasks}
                            onTaskUpdated={loadTasks}
                            onTaskOpen={(task: any) => void openTask(task)}
                            canPersistOrder={false}
                            columnsStorageId="my-tasks-widget"
                        />
                    )}
                </div>
                <div className={styles.myTasks__footer}>{taskCountTitle}</div>
            </div>

            {creationProject && (
                <TaskModal
                    isOpen={isTaskModalOpen}
                    onClose={() => setIsTaskModalOpen(false)}
                    createTask={createTask}
                    project={creationProject}
                    task={(creationAssigneeId || currentUserId) ? {
                        assignee_ids: [creationAssigneeId || currentUserId],
                    } : undefined}
                />
            )}
            {openedTask && (
                <TaskModal
                    isOpen
                    onClose={() => setOpenedTask(null)}
                    task={openedTask}
                    project={openedTask.project || listProject}
                    createTask={updateOpenedTask}
                />
            )}
        </div>
    )
}
